import React, { memo, useCallback } from "react";
import {
  SelectInput,
  SelectInputTheme
} from "../../../../Forms/Inputs/DropdownSelect/React";
import { FormSelectItem } from "../../types";
import InputWrapperWithHeading from "../shared/inputWrapperWithHeading";

interface ValidatedSelectInputFieldProps {
  id: string;
  name?: string;
  label?: string;
  description?: string;
  errorMessage?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  title?: string;
  value?: string;
  items: FormSelectItem[];
  required?: boolean;
  isValid: boolean;
  hasBlurred: boolean;
  formValidated: boolean;
  theme?: SelectInputTheme;
  onSearchCallback?: (input?: string) => void;
  onSelectedChanged?: (item: FormSelectItem) => void;
  onBlur?: () => void;
}

/**
 * ValidatedSelectInputField is a reusable form field that combines InputWrapperWithHeading
 * and SelectInput with built-in validation logic.
 */
export const ValidatedSelectInputField = memo(function ValidatedSelectInputField({
  id,
  name,
  label,
  description,
  errorMessage,
  placeholder,
  searchPlaceholder,
  title,
  value,
  items,
  required = false,
  isValid,
  hasBlurred,
  formValidated,
  theme,
  onSearchCallback,
  onSelectedChanged,
  onBlur
}: ValidatedSelectInputFieldProps) {
  const handleSelectedChanged = useCallback(
    (item: FormSelectItem) => {
      if (onSelectedChanged) {
        onSelectedChanged(item);
      }
    },
    [onSelectedChanged]
  );

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  const showError = !isValid && (formValidated || hasBlurred);

  return (
    <InputWrapperWithHeading
      id={id}
      label={label}
      description={description}
      errorMessage={errorMessage}
      required={required}
      invalid={showError}
    >
      <SelectInput
        id={id}
        items={items}
        title={title}
        name={name}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        onSearchCallback={onSearchCallback}
        onSelectedChanged={handleSelectedChanged}
        value={value}
        onBlur={handleBlur}
        invalid={showError}
        theme={theme}
      />
    </InputWrapperWithHeading>
  );
});

export default ValidatedSelectInputField;
